/**
 * Landing target for /deals/:citySlug -- the country-wide, per-city SEO
 * pages (see coupon_business/coupons/bot_views.py's deals_by_city_ssr for
 * the bot-served equivalent of this same URL).
 *
 * Real (non-bot) visitors hitting one of these URLs land here first: this
 * screen resolves the :citySlug param to a real city via the backend
 * (locations/resolveCitySlug -- GET /locations/cities/resolve/, same
 * slug-parsing logic as the SSR view), then immediately hands off to
 * CouponsListAll with that city pre-selected via `preSelectedCities` --
 * the exact same navigation-state handoff Home's hero search button
 * already uses (see Home/index.js's hero-search-btn onClick). This screen
 * never renders any real content itself; it's a pure resolve-then-redirect
 * step, mounted as a plain top-level route (no CustomerHeader) since
 * there's nothing here for a header to sit above.
 *
 * If the slug doesn't resolve to a real city (bad/stale link, mistyped
 * slug), redirect to CouponsListAll unfiltered rather than stranding the
 * visitor on a dead intermediate route.
 */
import { useEffect } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { actions as locationActions } from 'coupon-common/src/modules/locations';

import { RouteNames } from '../../containers/App/routeNames';
import { actions as cityActions } from 'coupon-common/src/modules/cities';

const DealsByCityRedirect = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { citySlug } = useParams();

  const resolution = useSelector(state => state.locations.citySlugResolution);

  useEffect(() => {
    if (citySlug) {
      dispatch(locationActions.resolveCitySlug(citySlug));
    }
  }, [citySlug, dispatch]);

  useEffect(() => {
    if (!resolution.fetched) return;

    if (resolution.data) {
      dispatch(cityActions.setActiveCity(resolution.data.name, resolution.data.id));
      navigate(RouteNames.COUPONS_LISTS_ALL, {
        replace: true,
        state: {
          preSelectedCities: [{ id: resolution.data.id, name: resolution.data.name }],
        },
      });
    } else {
      navigate(RouteNames.COUPONS_LISTS_ALL, { replace: true });
    }
  }, [resolution, navigate, dispatch]);

  useEffect(() => {
    if (!resolution.fetched) return;

    if (resolution.data) {
      navigate(RouteNames.COUPONS_LISTS_ALL, {
        replace: true,
        state: {
          preSelectedCities: [{ id: resolution.data.id, name: resolution.data.name }],
        },
      });
    } else {
      // Slug didn't resolve to a real city -- don't strand the visitor on
      // a dead intermediate route.
      navigate(RouteNames.COUPONS_LISTS_ALL, { replace: true });
    }
  }, [resolution, navigate]);

  return null;
};

export default DealsByCityRedirect;